export type AssistantViewport = { width: number; height: number };
export type AssistantPosition = { x: number; y: number; viewportWidth?: number; viewportHeight?: number };
export const ASSISTANT_BUTTON_SIZE = 56;
const MARGIN = 20;

function clamp(value: number, extent: number) {
  const max = Math.max(0, extent - ASSISTANT_BUTTON_SIZE - MARGIN);
  return Math.max(Math.min(MARGIN, max), Math.min(max, value));
}
export function hasPositionViewport(position: AssistantPosition | null | undefined): boolean {
  return !!position && Number.isFinite(position.viewportWidth) && Number.isFinite(position.viewportHeight)
    && position.viewportWidth! > 0 && position.viewportHeight! > 0;
}

/** Defaults dock outside the centered desktop app; mobile clears its bottom controls. */
export function resolveAssistantPosition(saved: AssistantPosition | null, viewport: AssistantViewport) {
  const fallback = {
    x: clamp(viewport.width - ASSISTANT_BUTTON_SIZE - MARGIN, viewport.width),
    y: clamp(viewport.height - ASSISTANT_BUTTON_SIZE - (viewport.width < 768 ? 100 : MARGIN), viewport.height),
  };
  if (!saved || !Number.isFinite(saved.x) || !Number.isFinite(saved.y)) return fallback;
  // Honor older device-local positions. Only viewport-aware positions cross devices.
  if (!hasPositionViewport(saved)) return { x: clamp(saved.x, viewport.width), y: clamp(saved.y, viewport.height) };
  const oldWidth = saved.viewportWidth!, oldHeight = saved.viewportHeight!;
  const x = clamp(saved.x, oldWidth), y = clamp(saved.y, oldHeight);
  const right = x + ASSISTANT_BUTTON_SIZE / 2 >= oldWidth / 2;
  const bottom = y + ASSISTANT_BUTTON_SIZE / 2 >= oldHeight / 2;
  return {
    x: clamp(right ? viewport.width - (oldWidth - x) : x, viewport.width),
    y: clamp(bottom ? viewport.height - (oldHeight - y) : y, viewport.height),
  };
}

/** Save only deliberate drops, never a temporary resize/keyboard clamp. */
export function saveAssistantPosition(position: { x: number; y: number }, viewport: AssistantViewport): AssistantPosition {
  return {
    x: clamp(Number.isFinite(position.x) ? position.x : viewport.width, viewport.width),
    y: clamp(Number.isFinite(position.y) ? position.y : viewport.height, viewport.height),
    viewportWidth: viewport.width, viewportHeight: viewport.height,
  };
}
